import { FormEvent, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createStandortLieferbedingung,
  deleteStandortLieferbedingung,
  fetchAdressen,
  fetchLieferbedingungen,
  fetchStandortLieferbedingungen,
} from "@/lib/api"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { Badge, Button, Select } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

/**
 * Lieferbedingungen-Zuordnung je Standort (Adresse) einer Organisation
 * (Ninox `HF` Standort2Lieferbed) — AP-2.8. Zeigt/erlaubt die Zuordnung für
 * alle Adressen der Organisation, die als Standort markiert sind.
 */
export function LieferbedingungenZuordnungSection({
  organisationId,
}: {
  organisationId: string
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [adresseId, setAdresseId] = useState("")
  const [lieferbedingungId, setLieferbedingungId] = useState("")
  const [standard, setStandard] = useState(false)
  const [loeschenId, setLoeschenId] = useState<string | null>(null)

  const { data: adressen } = useQuery({
    queryKey: ["adressen", organisationId],
    queryFn: () => fetchAdressen(organisationId),
  })

  const standorte = useMemo(() => (adressen ?? []).filter((a) => a.istStandort), [adressen])

  const { data: lieferbedingungen } = useQuery({
    queryKey: ["lieferbedingungen"],
    queryFn: () => fetchLieferbedingungen(true),
  })

  const { data: zuordnungen, isLoading } = useQuery({
    queryKey: ["standort-lieferbedingungen", "organisation", organisationId, standorte.map((s) => s.id)],
    queryFn: async () => {
      const alle = await Promise.all(standorte.map((s) => fetchStandortLieferbedingungen(s.id)))
      return alle.flat()
    },
    enabled: standorte.length > 0,
  })

  const adressenById = useMemo(() => new Map((adressen ?? []).map((a) => [a.id, a])), [adressen])
  const lieferbedingungenById = useMemo(
    () => new Map((lieferbedingungen ?? []).map((l) => [l.id, l])),
    [lieferbedingungen],
  )

  const invalidieren = () =>
    queryClient.invalidateQueries({ queryKey: ["standort-lieferbedingungen"] })

  const anlegen = useMutation({
    mutationFn: () =>
      createStandortLieferbedingung({
        adresseId,
        lieferbedingungId,
        standard,
      }),
    onSuccess: () => {
      invalidieren()
      setAdresseId("")
      setLieferbedingungId("")
      setStandard(false)
      setZeigeNeu(false)
      toast.show("Lieferbedingung zugeordnet.", "success")
    },
    onError: () => toast.show("Zuordnung konnte nicht gespeichert werden.", "error"),
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteStandortLieferbedingung(id),
    onSuccess: () => {
      invalidieren()
      setLoeschenId(null)
      toast.show("Zuordnung gelöscht.", "success")
    },
    onError: () => toast.show("Zuordnung konnte nicht gelöscht werden.", "error"),
  })

  function onSubmitNeu(e: FormEvent) {
    e.preventDefault()
    if (!adresseId || !lieferbedingungId) return
    anlegen.mutate()
  }

  if (standorte.length === 0) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-2 text-lg font-semibold text-slate-900">
          Lieferbedingungen je Standort
        </h2>
        <p className="text-sm text-slate-500">
          Diese Organisation hat noch keine als Standort markierte Adresse — Lieferbedingungen
          lassen sich erst zuordnen, sobald unter „Adressen &amp; Standorte" ein Standort angelegt
          wurde.
        </p>
      </section>
    )
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Lieferbedingungen je Standort</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Zuordnung hinzufügen
        </button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <SkeletonZeile className="h-10 w-full" />
        </div>
      )}

      <ul className="space-y-2">
        {zuordnungen?.map((z) => (
          <li
            key={z.id}
            className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-sm"
          >
            <div>
              <p className="font-medium text-slate-900">
                {lieferbedingungenById.get(z.lieferbedingungId)?.incoterm ?? "—"}{" "}
                {lieferbedingungenById.get(z.lieferbedingungId)?.name ?? ""}
                {z.standard && (
                  <Badge tone="slate" className="ml-2">
                    Standard
                  </Badge>
                )}
              </p>
              <p className="text-slate-500">
                Standort: {adressenById.get(z.adresseId)?.standortbezeichnung ?? adressenById.get(z.adresseId)?.ort ?? "—"}
              </p>
            </div>
            {loeschenId === z.id ? (
              <span className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-600">Wirklich löschen?</span>
                <button
                  type="button"
                  onClick={() => loeschen.mutate(z.id)}
                  disabled={loeschen.isPending}
                  className="rounded-md bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Ja, löschen
                </button>
                <button
                  type="button"
                  onClick={() => setLoeschenId(null)}
                  className="rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Abbrechen
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setLoeschenId(z.id)}
                className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                Löschen
              </button>
            )}
          </li>
        ))}
      </ul>

      {!isLoading && (zuordnungen?.length ?? 0) === 0 && !zeigeNeu && (
        <EmptyState
          text="Noch keine Lieferbedingung zugeordnet."
          aktion={{ label: "+ Zuordnung hinzufügen", onClick: () => setZeigeNeu(true) }}
        />
      )}

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="lieferbedingung-zuordnung-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue Zuordnung</SheetTitle>
            <SheetDescription>Lieferbedingung einem Standort zuordnen.</SheetDescription>
          </SheetHeader>
          <form id="lb-zuordnung-neu-form" onSubmit={onSubmitNeu} className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Standort *</label>
              <Select required value={adresseId} onChange={(e) => setAdresseId(e.target.value)}>
                <option value="">— auswählen —</option>
                {standorte.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.standortbezeichnung ?? s.ort ?? s.id}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Lieferbedingung *</label>
              <Select
                required
                value={lieferbedingungId}
                onChange={(e) => setLieferbedingungId(e.target.value)}
              >
                <option value="">— auswählen —</option>
                {lieferbedingungen?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.incoterm} {l.name ?? ""}
                  </option>
                ))}
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={standard}
                onChange={(e) => setStandard(e.target.checked)}
              />
              Als Standard für diesen Standort setzen
            </label>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="lb-zuordnung-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Zuordnung speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}
